import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const initialForm = {
  customerName: '',
  customerEmail: '',
  expiresAtUtc: '',
  maxActivations: 1,
};

const statusOptions = ['Active', 'Suspended', 'Revoked'];

const formatDate = (value) => {
  if (!value) {
    return 'NA';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'NA' : date.toLocaleString();
};

const toDateInputValue = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().slice(0, 16);
};

const fetchIssuedBamrocLicenses = async (jwt) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminbamroclicense/all-licenses`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    }
  );

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || 'Failed to load licenses.');
  }

  return result.licenses || [];
};

const AdminLicenseManagementView = () => {
  const [formData, setFormData] = useState({ ...initialForm, expiresAtUtc: toDateInputValue() });
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [issuedLicense, setIssuedLicense] = useState(null);

  const jwt = localStorage.getItem('jwttoken') || sessionStorage.getItem('jwttoken');

  const loadLicenses = async () => {
    setLoading(true);
    try {
      const nextLicenses = await fetchIssuedBamrocLicenses(jwt);
      setLicenses(nextLicenses);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadLicensesOnOpen = async () => {
      setLoading(true);
      try {
        const nextLicenses = await fetchIssuedBamrocLicenses(jwt);
        setLicenses(nextLicenses);
      } catch (error) {
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    };

    loadLicensesOnOpen();
  }, [jwt]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: name === 'maxActivations' ? Number(value) : value,
    }));
  };

  const handleIssueLicense = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    setIssuedLicense(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminbamroclicense/issue-license`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            customerName: formData.customerName,
            customerEmail: formData.customerEmail,
            expiresAtUtc: new Date(formData.expiresAtUtc).toISOString(),
            maxActivations: formData.maxActivations,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to issue license.');
      }

      setIssuedLicense(result.license);
      setMessage({ type: 'success', text: result.message || 'License issued successfully.' });
      setFormData({ ...initialForm, expiresAtUtc: toDateInputValue() });
      await loadLicenses();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (licenseKey, status) => {
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminbamroclicense/update-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ licenseKey, status }),
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to update license status.');
      }

      setLicenses((current) =>
        current.map((item) =>
          item.licenseKey === licenseKey
            ? { ...item, status }
            : item
        )
      );
      setMessage({ type: 'success', text: result.message || 'License updated successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#5C4033' }}>
        BAMROC License Management
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: '#7A6855' }}>
        Issue BAMROC licenses in the cloud and control the activation status used by the plugin.
      </Typography>

      {message.text && (
        <Alert severity={message.type || 'info'} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Issue New License
        </Typography>
        <Box component="form" onSubmit={handleIssueLicense}>
          <Stack spacing={2}>
            <TextField
              label="Customer Name"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Customer Email"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Expiry (UTC)"
              name="expiresAtUtc"
              type="datetime-local"
              value={formData.expiresAtUtc}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Max Activations"
              name="maxActivations"
              type="number"
              value={formData.maxActivations}
              onChange={handleChange}
              inputProps={{ min: 1 }}
              required
              fullWidth
            />
            <Box>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{
                  backgroundColor: '#CC9A39',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#B8860B' },
                }}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Issue License'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {issuedLicense && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: '#FFF8ED' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Newly Issued Credentials
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>License Key:</strong> {issuedLicense.licenseKey}
          </Typography>
          <Typography sx={{ mb: 1, wordBreak: 'break-all' }}>
            <strong>Secret Key:</strong> {issuedLicense.secretKey}
          </Typography>
          <Typography sx={{ color: '#7A6855' }}>
            This is the full secret returned by the backend. Store or send it securely.
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Issued Licenses
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>License Key</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Secret</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Activations</TableCell>
                  <TableCell>Last Validated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licenses.map((license) => (
                  <TableRow key={license.licenseKey}>
                    <TableCell sx={{ fontWeight: 600 }}>{license.licenseKey}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{license.customerName || 'NA'}</Typography>
                      <Typography variant="body2" sx={{ color: '#7A6855' }}>
                        {license.customerEmail || 'NA'}
                      </Typography>
                    </TableCell>
                    <TableCell>{license.maskedSecretKey || 'Hidden'}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <TextField
                        select
                        size="small"
                        value={license.status}
                        onChange={(event) => handleStatusChange(license.licenseKey, event.target.value)}
                        fullWidth
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>{formatDate(license.expiresAtUtc)}</TableCell>
                    <TableCell>
                      {license.activationCount} / {license.maxActivations}
                    </TableCell>
                    <TableCell>{formatDate(license.lastValidatedAtUtc)}</TableCell>
                  </TableRow>
                ))}
                {licenses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No licenses issued yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminLicenseManagementView;
